'use client';

import { useHyperliquidStore } from '@/stores/hyperliquidStore';
import { Skeleton } from '@/components/ui/skeleton';

export default function MarketInfo() {
  const selectedAssetNumber = useHyperliquidStore(state => state.selectedAssetNumber);
  const metaAndAssetCtxs = useHyperliquidStore(state => state.metaAndAssetCtxs);
  const activeAssetCtx = useHyperliquidStore(state => state.activeAssetCtx);
  const allMids = useHyperliquidStore(state => state.mids);

  const selectedAssetCtx = metaAndAssetCtxs[selectedAssetNumber];

  // Calculate 24h change
  const price = activeAssetCtx ? parseFloat(activeAssetCtx.markPx) : undefined;
  const prevPrice = activeAssetCtx ? parseFloat(activeAssetCtx.prevDayPx) : undefined;
  const change24h = price && prevPrice ? price - prevPrice : undefined;
  const change24hPercent = change24h && prevPrice ? (change24h / prevPrice) * 100 : undefined;
  const isPositive = change24hPercent && change24hPercent >= 0;

  // Get oracle price
  const oraclePrice = activeAssetCtx ? parseFloat(activeAssetCtx.oraclePx) : undefined;

  // Format volume
  const volume24h = activeAssetCtx ? parseFloat(activeAssetCtx.dayNtlVlm) : undefined;
  const formatNumber = (num: number, decimals: number = 2) => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // Get funding rate
  const fundingRate = activeAssetCtx ? parseFloat(activeAssetCtx.funding) * 100 : undefined;
  const selectedName = 'BTC';

  // Get open interest
  const openInterest = activeAssetCtx && price ? parseFloat(activeAssetCtx.openInterest) * price : undefined;

  return (
    <div className='flex h-fit flex-col border-b p-4 lg:flex-row lg:items-center lg:gap-10'>
      <div className='grid grid-cols-2 gap-4 lg:flex lg:grid-cols-1 lg:items-center'>
        <div className='flex items-center gap-1'>
          <img
            src={`https://app.hyperliquid.xyz/coins/${selectedName}.svg`}
            alt={selectedName}
            className='size-5'
          />
          <span className='text-base font-semibold text-white'>{selectedName}/USDT</span>
        </div>
        {/* <div className='flex flex-col justify-center gap-1 leading-none lg:hidden'>
          {price ? (
            <span className='font-medium text-white'>${formatNumber(price, 0)}</span>
          ) : (
            <Skeleton className='h-3 w-20' />
          )}
        </div> */}
      </div>

      {/* Right Section - Stats */}
      <div className='grid grid-cols-2 items-center gap-x-5 gap-y-3 text-xs lg:flex lg:gap-4'>
        <div className='hidden flex-col gap-1 leading-none lg:flex'>
          <span className='tracking-tight text-[#EBEBEB99]'>Mark</span>
          {price ? (
            <span className='font-medium text-white'>${formatNumber(price, 0)}</span>
          ) : (
            <Skeleton className='h-3 w-20' />
          )}
        </div>
        <div className='hidden flex-col gap-1 leading-none lg:flex'>
          <span className='tracking-tight text-[#EBEBEB99]'>Oracle</span>
          {oraclePrice ? (
            <span className='font-medium text-white'>${formatNumber(oraclePrice, 0)}</span>
          ) : (
            <Skeleton className='h-3 w-20' />
          )}
        </div>
        <div className='flex flex-col gap-1 leading-none lg:hidden'>
          <span className='tracking-tight text-[#EBEBEB99]'>Mark / Oracle</span>
          {price && oraclePrice ? (
            <span className='font-medium text-white'>
              ${formatNumber(price, 0)} / ${formatNumber(oraclePrice, 0)}
            </span>
          ) : (
            <Skeleton className='h-3 w-20' />
          )}
        </div>
        <div className='flex flex-col gap-1 leading-none'>
          <span className='tracking-tight text-[#EBEBEB99]'>24h Volume</span>
          {volume24h ? (
            <span className='font-medium text-white'>${formatNumber(volume24h, 0)}</span>
          ) : (
            <Skeleton className='h-3 w-20' />
          )}
        </div>{' '}
        <div className='hidden flex-col gap-1 leading-none lg:flex'>
          <span className='tracking-tight text-[#EBEBEB99]'>24h Change</span>
          {change24hPercent ? (
            <span className={`font-medium ${isPositive ? 'text-[#00BE00]' : 'text-red-400'}`}>
              {change24hPercent >= 0 ? '+' : ''}
              {formatNumber(change24hPercent, 2)}%
            </span>
          ) : (
            <Skeleton className='h-3 w-20' />
          )}
        </div>
        <div className='flex flex-col gap-1 leading-none'>
          <span className='tracking-tight text-[#EBEBEB99]'>Open Interest</span>
          {openInterest ? (
            <span className='font-medium text-white'>${formatNumber(openInterest, 0)}</span>
          ) : (
            <Skeleton className='h-3 w-20' />
          )}
        </div>
        <div className='hidden flex-col gap-1 leading-none lg:flex'>
          <span className='tracking-tight text-[#EBEBEB99]'>Funding</span>
          {fundingRate ? (
            <span className={`font-medium ${fundingRate >= 0 ? 'text-[#00BE00]' : 'text-red-400'}`}>
              {fundingRate >= 0 ? '+' : ''}
              {formatNumber(fundingRate, 4)}%
            </span>
          ) : (
            <Skeleton className='h-3 w-20' />
          )}
        </div>
        <div className='hidden flex-col gap-1 leading-none lg:flex'>
          <span className='tracking-tight text-[#EBEBEB99]'>Countdown</span>
          <span className='font-medium text-white'>00:42:48</span>
        </div>
        <div className='flex flex-col gap-1 leading-none lg:hidden'>
          <span className='tracking-tight text-[#EBEBEB99]'>Funding / Countdown</span>
          {fundingRate ? (
            <span className={`font-medium ${fundingRate >= 0 ? 'text-[#00BE00]' : 'text-red-400'}`}>
              {fundingRate >= 0 ? '+' : ''}
              {formatNumber(fundingRate, 4)}% <span className='font-medium text-white'>/ 00:42:48</span>
            </span>
          ) : (
            <Skeleton className='h-3 w-20' />
          )}
        </div>
      </div>
    </div>
  );
}
